import { Injectable, signal } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { User, CreditData, DatosBancarios } from '../models/credit-simulator-interface';
import { BankAccount } from '../models/response/account.response';
import { WidgetData } from '../models/widget-data.interface';
import { SendVerificationCodeResponse } from '../models/response/send-verification-code.response';
import { PaymentCard } from '../models/payment-card.model';

export interface AppDataSnapshot {
  validatedDocument: string | undefined;
  captchaToken: string | undefined;
  newLine: number | undefined;
  maxLineAmount: number | undefined;
  accounts: BankAccount[];
  user: User;
  creditData: CreditData | null;
  bankingData: DatosBancarios | null;
  customerPhone: string | null;
  selectedBankAccount: BankAccount | null;
  loggedByUrl: boolean;
  isCardGoingToBeProvided: boolean;
  selectedCard: PaymentCard | undefined;
  whiteListFlowData: { lineaId: number; enabled: boolean } | undefined;
  widgetData: WidgetData | null;
  ip: string | undefined;
}

@Injectable({
  providedIn: 'root',
})
export class DataService {
  /** Validated document**/
  private validatedDocumentSignal = signal<undefined | string>(undefined);
  public validatedDocument = this.validatedDocumentSignal.asReadonly();

  /** Google captcha validation**/
  private captchaTokenSignal = signal<undefined | string>(undefined);
  public captchaToken = this.captchaTokenSignal.asReadonly();

  // New line data
  private newLineSignal = signal<undefined | number>(undefined);
  public newLine = this.newLineSignal.asReadonly();

  // Max line amount
  private maxLineAmountSignal = signal<undefined | number>(undefined);
  public maxLineAmount = this.maxLineAmountSignal.asReadonly();

  //Accounts
  private accountsSignal = signal<BankAccount[]>([]);
  public accounts = this.accountsSignal.asReadonly();

  private user$ = new BehaviorSubject<User>(User.Empty());
  private creditData$ = new BehaviorSubject<CreditData | null>(null);
  private codeData$ = new BehaviorSubject<SendVerificationCodeResponse | null>(null);
  private bankingData$ = new BehaviorSubject<DatosBancarios | null>(null);
  private customerPhone$ = new BehaviorSubject<string | null>(null);
  private selectedBankAccount$ = new BehaviorSubject<BankAccount | null>(null);
  private loggedByUrl$ = new BehaviorSubject(false);
  private isCardGoingToBeProvided$ = new BehaviorSubject(false);
  private selectedCard$ = new BehaviorSubject<PaymentCard | undefined>(undefined);
  private isPlatformInMaintenanceSignal = signal(false);
  public isPlatformInMaintenance = this.isPlatformInMaintenanceSignal.asReadonly();
  private whiteListFlowDataSignal = signal<{ lineaId: number; enabled: boolean } | undefined>(undefined);
  public whiteListFlowData = this.whiteListFlowDataSignal.asReadonly();
  private widgetData$ = new BehaviorSubject<WidgetData | null>(null);
  private ipSignal = signal<string | undefined>(undefined);
  public readonly ip = this.ipSignal.asReadonly();

  public getAppDataSnapshot(): AppDataSnapshot {
    const validatedDocument = this.validatedDocument();
    const captchaToken = this.captchaToken();
    const newLine = this.newLine();
    const maxLineAmount = this.maxLineAmount();
    const accounts = this.accounts();
    const user = this.user$.getValue();
    const creditData = this.creditData$.getValue();
    const bankingData = this.bankingData$.getValue();
    const customerPhone = this.customerPhone$.getValue();
    const selectedBankAccount = this.selectedBankAccount$.getValue();
    const loggedByUrl = this.loggedByUrl$.getValue();
    const isCardGoingToBeProvided = this.isCardGoingToBeProvided$.getValue();
    const selectedCard = this.selectedCard$.getValue();
    const whiteListFlowData = this.whiteListFlowData();
    const widgetData = this.widgetData$.getValue();
    const ip = this.ip();

    return {
      validatedDocument,
      captchaToken,
      newLine,
      maxLineAmount,
      accounts,
      user,
      creditData,
      bankingData,
      customerPhone,
      selectedBankAccount,
      loggedByUrl,
      isCardGoingToBeProvided,
      selectedCard,
      whiteListFlowData,
      widgetData,
      ip,
    };
  }

  public setAppDataSnapshot(data: AppDataSnapshot): void {
    this.setCreditData(data.creditData);
    this.setValidatedDocument(data.validatedDocument);
    this.setCaptchaToken(data.captchaToken);
    this.setNewLine(data.newLine);
    this.setMaxLineAmount(data.maxLineAmount);
    this.setBankAccounts(data.accounts);
    this.updateUserData(data.user);
    this.setBankingData(data.bankingData);
    this.setPhone(data.customerPhone);
    this.setBankAccount(data.selectedBankAccount);
    this.setLoggedByUrl(data.loggedByUrl);
    this.setIsCardGoingToBeProvided(data.isCardGoingToBeProvided);
    this.setSelectedCard(data.selectedCard);
    this.setWhiteListFlowData(data.whiteListFlowData);
    this.setWidgetData(data.widgetData);
    this.setIp(data.ip);
  }

  public setIp(ip: string | undefined): void {
    this.ipSignal.set(ip);
  }

  /** Validated document**/
  setValidatedDocument(document: string | undefined): void {
    this.validatedDocumentSignal.set(document);
  }

  /** Google captcha validation**/
  setCaptchaToken(token: string | undefined): void {
    this.captchaTokenSignal.set(token);
  }

  // New line
  setNewLine(line: number | undefined): void {
    this.newLineSignal.set(line);
  }

  // Max line amount
  setMaxLineAmount(line: number | undefined): void {
    this.maxLineAmountSignal.set(line);
  }

  // Bank account
  setBankAccounts(accounts: BankAccount[]): void {
    this.accountsSignal.set(accounts);
  }

  public setWhiteListFlowData(data: { lineaId: number; enabled: boolean } | undefined): void {
    this.whiteListFlowDataSignal.set(data);
  }

  public setIsPlatformInMaintenance(maintenance: boolean): void {
    this.isPlatformInMaintenanceSignal.set(maintenance);
  }

  //Giveaway data
  private link$ = new BehaviorSubject<string | null>(null);
  private isGiveAwayActive$ = new BehaviorSubject(false);

  public setSelectedCard(data: PaymentCard | undefined): void {
    this.selectedCard$.next(data);
  }

  public getSelectedCard$(): Observable<PaymentCard | undefined> {
    return this.selectedCard$.asObservable();
  }

  public setCodeData(data: SendVerificationCodeResponse): void {
    this.codeData$.next(data);
  }

  public getCodeData$(): Observable<SendVerificationCodeResponse | null> {
    return this.codeData$.asObservable();
  }

  public setWidgetData(widgetData: WidgetData | null): void {
    this.widgetData$.next(widgetData);
  }

  public setLink(link: string, active = true): void {
    this.link$.next(link);
    this.isGiveAwayActive$.next(active);
  }

  public getIsGiveawayActive$(): Observable<boolean> {
    return this.isGiveAwayActive$.asObservable();
  }

  public getGiveawayLink$(): Observable<string | null> {
    return this.link$.asObservable();
  }

  public getWidgetData(): Observable<WidgetData | null> {
    return this.widgetData$.asObservable();
  }

  public setBankAccount(account: BankAccount | null): void {
    this.selectedBankAccount$.next(account);
  }

  public getBankAccountObservable(): Observable<BankAccount | null> {
    return this.selectedBankAccount$;
  }

  public getCreditDataObservable(): Observable<CreditData | null> {
    return this.creditData$.asObservable();
  }

  public getCreditData(): CreditData | null {
    return this.creditData$.value;
  }

  public getBankingDataObservable(): Observable<DatosBancarios | null> {
    return this.bankingData$.asObservable();
  }

  public getUserObservable(): Observable<User> {
    return this.user$.asObservable();
  }

  public getCustomerPhoneObservable(): Observable<string | null> {
    return this.customerPhone$;
  }

  public async updateUserData(user: Partial<User>): Promise<void> {
    this.user$.next({ ...this.user$.value, ...user });
  }

  public setBankingData(data: DatosBancarios | null): void {
    this.bankingData$.next(data);
  }

  public setCreditData(data: CreditData | null): void {
    if (data) this.creditData$.next({ ...data });
    else this.creditData$.next(null);
  }

  public updateCreditData(data: Partial<CreditData>): void {
    if (this.creditData$.value) this.creditData$.next({ ...this.creditData$.value, ...data });
  }

  public setPhone(phone: string | null): void {
    this.customerPhone$.next(phone);
  }

  public setLoggedByUrl(logged: boolean): void {
    this.loggedByUrl$.next(logged);
  }

  public getLoggedByUrlObservable(): Observable<boolean> {
    return this.loggedByUrl$.asObservable();
  }

  public setIsCardGoingToBeProvided(provided: boolean): void {
    this.isCardGoingToBeProvided$.next(provided);
  }

  public getIsCardGoingToBeProvidedAsObservable(): Observable<boolean> {
    return this.isCardGoingToBeProvided$.asObservable();
  }
}
